import { useCallback, useMemo, useState } from 'react';
import { Stepper } from 'Components/organisms';
import { Typography } from 'Components/atoms';
import { CustomActionDto } from '@ampsec/platform-client';
import { ACTION_LABELS, CUSTOM_ACTION_HEADERS, SUPPORTING_TEXT } from 'Core-utils/constants/constants';
import { MicrosoftIntuneGeneralStep } from './MicrosoftIntuneGeneralStep';
import { MicrosoftIntuneRebootConfigStep } from './MicrosoftIntuneRebootConfigStep';

interface MicrosoftIntuneRebootStepperFormProps {
    data: CustomActionDto;
    handleFieldChange: (id: string, value: string) => void;
    handleSave: () => void;
}

const MicrosoftIntuneRebootStepperForm = ({
    data,
    handleFieldChange,
    handleSave,
}: MicrosoftIntuneRebootStepperFormProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState<string[]>([]);
    const lastStepIndex = 1;

    const validateStep = useCallback(
        (step: number): string[] => {
            const formErrors: string[] = [];
            if (step === 0 && !data.displayValue?.trim()) {
                formErrors.push(`${ACTION_LABELS[2]} ${SUPPORTING_TEXT}`);
            }
            return formErrors;
        },
        [data.displayValue],
    );

    const handleBackButtonClick = () => {
        setErrors([]);
        setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
    };

    const handleNextButtonClick = () => {
        const formErrors = validateStep(currentStep);
        if (formErrors.length > 0) {
            setErrors(formErrors);
            return;
        }
        setErrors([]);
        setCurrentStep((prevStep) => Math.min(prevStep + 1, lastStepIndex));
    };

    const isStepValid = useCallback((step: number) => validateStep(step).length === 0, [validateStep]);

    const isFormValid = useMemo(() => isStepValid(0) && isStepValid(1), [isStepValid]);

    const nextDisabled = currentStep === 0 && !isStepValid(0);
    const saveDisabled = !isFormValid;

    const handleSaveClick = () => {
        if (!isFormValid) {
            const formErrors = [...validateStep(0), ...validateStep(1)];
            setErrors(formErrors);
            if (!data.displayValue?.trim()) {
                setCurrentStep(0);
            }
            return;
        }
        setErrors([]);
        handleSave();
    };

    const renderHeading = (title: string) => (
        <Typography variant="body12" color="text-high-emphasis" style={{ textTransform: 'uppercase' }}>
            {title}
        </Typography>
    );

    const steps = [
        {
            stepperName: CUSTOM_ACTION_HEADERS[0],
            stepperContent: (
                <>
                    {renderHeading(CUSTOM_ACTION_HEADERS[0])}
                    <MicrosoftIntuneGeneralStep formData={data} handleFieldChange={handleFieldChange} errors={errors} />
                </>
            ),
        },
        {
            stepperName: 'Reboot Configuration',
            stepperContent: (
                <div className="microsoft-intune-reboot-step__content">
                    {renderHeading('Reboot configuration')}
                    <MicrosoftIntuneRebootConfigStep />
                </div>
            ),
        },
    ];

    return (
        <div className="microsoft-intune-reboot-stepper h-100">
            <Stepper
                steps={steps}
                currentStep={currentStep}
                handleBackButtonClick={handleBackButtonClick}
                handleNextButtonClick={handleNextButtonClick}
                handleSave={handleSaveClick}
                nextDisabled={nextDisabled}
                saveDisabled={saveDisabled}
            />
        </div>
    );
};

export default MicrosoftIntuneRebootStepperForm;
